package com.milta.feature.team.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import com.milta.feature.team.domain.usecase.LinkDriverUseCase
import com.milta.feature.team.domain.usecase.RemoveDriverUseCase
import kotlinx.coroutines.launch

/**
 * ViewModel for the web dashboard's team management screen (MIL-49). Consolidates what mobile
 * splits across two screens/ViewModels — [EnterpriseTeamViewModel] (list + add-driver-by-code)
 * and [DriverDetailViewModel] (remove-driver confirmation) — into a single [WebTeamState], since
 * the web dashboard shows the removal confirmation as a dialog on top of the same list rather than
 * navigating to a dedicated detail screen. Reuses the exact same use cases as mobile, nothing new.
 *
 * enterpriseId resolution: no JWT claim exposes the current Enterprise account's enterpriseId on
 * the client today — mirrors [WebImportRouteViewModel.loadDrivers]/[EnterpriseTeamViewModel.loadTeam]:
 * [GetMyTeamUseCase] lists every organisation the current account is attached to, and the first one
 * is used as the target enterpriseId (a standard Enterprise account has exactly one).
 */
class WebTeamViewModel(
    private val getMyTeam: GetMyTeamUseCase,
    private val getEnterpriseDrivers: GetEnterpriseDriversUseCase,
    private val linkDriver: LinkDriverUseCase,
    private val removeDriver: RemoveDriverUseCase,
) : MviViewModel<WebTeamState, WebTeamIntent>(WebTeamState()) {

    override fun onIntent(intent: WebTeamIntent) {
        when (intent) {
            WebTeamIntent.LoadTeam -> loadTeam()
            is WebTeamIntent.UpdateCodeInput -> setState { copy(codeInput = intent.code, addDriverError = null) }
            WebTeamIntent.SubmitCode -> submitCode()
            WebTeamIntent.ClearAddDriverError -> setState { copy(addDriverError = null) }
            is WebTeamIntent.RequestRemoval -> setState {
                copy(pendingRemovalDriverId = intent.driverId, removalError = null)
            }
            WebTeamIntent.CancelRemoval -> setState { copy(pendingRemovalDriverId = null, removalError = null) }
            WebTeamIntent.ConfirmRemoval -> confirmRemoval()
        }
    }

    /** Unlike [WebImportRouteViewModel.loadDrivers] (a secondary, non-blocking list inside a
     * multi-step wizard), the driver list here IS the screen's primary content — a load failure
     * must not collapse into the same state shape as a genuine empty team (see [WebTeamState.error]
     * doc / mobile's [TeamViewModel.loadTeam]), otherwise a manager whose driver fetch failed
     * (offline, 500, expired session) sees the identical "no drivers yet" hint as one with a
     * genuinely empty team, with no indication anything went wrong. */
    private fun loadTeam() {
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            val myTeamResult = getMyTeam()
            val enterpriseId = myTeamResult.getOrNull()?.firstOrNull()?.enterpriseId
            if (enterpriseId == null) {
                val error = myTeamResult.exceptionOrNull()?.let { if (it is ApiException) "server_error" else "offline" }
                setState { copy(isLoading = false, error = error) }
                return@launch
            }
            getEnterpriseDrivers(enterpriseId).fold(
                onSuccess = { drivers ->
                    setState { copy(isLoading = false, enterpriseId = enterpriseId, drivers = drivers, error = null) }
                },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLoading = false, enterpriseId = enterpriseId, error = errorCode) }
                },
            )
        }
    }

    /** Mirrors [EnterpriseTeamViewModel.addDriver]'s error mapping exactly — reads the server
     * message set by the backend rather than the HTTP status code. */
    private fun submitCode() {
        val enterpriseId = currentState.enterpriseId ?: return
        val code = currentState.codeInput.trim()
        if (code.isBlank()) {
            setState { copy(addDriverError = "invalid_code") }
            return
        }
        scope.launch {
            setState { copy(isSubmittingCode = true, addDriverError = null) }
            linkDriver(enterpriseId, code).fold(
                onSuccess = {
                    setState { copy(isSubmittingCode = false, codeInput = "") }
                    reloadDrivers(enterpriseId)
                },
                onFailure = { e ->
                    val errorCode = when {
                        e is ApiException && e.message == "error.validation.code" -> "invalid_code"
                        e is ApiException && e.message == "error.not_found" -> "not_found"
                        e is ApiException && e.message == "error.conflict.membership" -> "already_member"
                        e is ApiException && e.message == "error.conflict.linked_account_is_enterprise" -> "linked_account_is_enterprise"
                        e is ApiException -> "server_error"
                        else -> "offline"
                    }
                    setState { copy(isSubmittingCode = false, addDriverError = errorCode) }
                },
            )
        }
    }

    /** Mirrors [DriverDetailViewModel.confirmRemove]'s error mapping exactly. On failure the dialog
     * is kept open (unlike mobile, which closes it) — [WebTeamIntent.RequestRemoval]'s task
     * description asks the user to see the message next to the still-open dialog rather than
     * re-opening it. */
    private fun confirmRemoval() {
        val enterpriseId = currentState.enterpriseId ?: return
        val driverId = currentState.pendingRemovalDriverId ?: return
        scope.launch {
            setState { copy(isRemoving = true, removalError = null) }
            removeDriver(enterpriseId, driverId).fold(
                onSuccess = {
                    setState { copy(isRemoving = false, pendingRemovalDriverId = null) }
                    reloadDrivers(enterpriseId)
                },
                onFailure = { e ->
                    val errorCode = when {
                        e is ApiException && e.message == "error.conflict.driver_on_active_delivery" -> "on_active_delivery"
                        e is ApiException && e.message == "error.conflict.last_enterprise_owner" -> "last_owner"
                        e is ApiException && e.message == "error.not_found" -> "not_found"
                        e is ApiException -> "server_error"
                        else -> "offline"
                    }
                    setState { copy(isRemoving = false, removalError = errorCode) }
                },
            )
        }
    }

    private suspend fun reloadDrivers(enterpriseId: String) {
        getEnterpriseDrivers(enterpriseId).onSuccess { drivers -> setState { copy(drivers = drivers) } }
    }
}
